<template>
  <v-list density="compact">
    <template v-for="(section, sectionKey) in specifications" :key="sectionKey">
      <v-list-subheader v-if="section.items?.length">
        {{ section.title }}
      </v-list-subheader>
      <v-list-item
        v-for="(item, index) in section.items"
        :key="`${sectionKey}-${index}`"
      >
        <template v-slot:prepend>
          <v-icon :icon="section.icon" size="small" />
        </template>
        <v-tooltip location="top">
          <template v-slot:activator="{ props: tooltipProps }">
            <v-list-item-title v-bind="tooltipProps">{{ item.label }}</v-list-item-title>
          </template>
          <span>{{ item.label }}</span>
        </v-tooltip>
        <template v-slot:append>
          <v-tooltip location="top">
            <template v-slot:activator="{ props: tooltipProps }">
              <span v-bind="tooltipProps" class="spec-value">{{ item.value }}</span>
            </template>
            <span>{{ item.value }}</span>
          </v-tooltip>
        </template>
      </v-list-item>
    </template>
  </v-list>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { parseSpecValue, parseSpecItem } from '@/utils/modelDetails'

import type { TrimData } from '@/types'

const props = defineProps<{
  trim: TrimData | null
}>()

const sanitizeHtml = (text: string): string => {
  if (!text) return ''
  return text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .replace(/&amp;/g, '&') // Replace &amp; with &
    .replace(/&lt;/g, '<') // Replace &lt; with <
    .replace(/&gt;/g, '>') // Replace &gt; with >
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&#39;/g, "'") // Replace &#39; with '
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim()
}

const getSpecValue = (category: string, searchTerm: string): string => {
  const items = props.trim?.technicalSpecification?.[category]?.items || []
  const item = items.find((i: string) => i.includes(searchTerm))
  if (item) {
    const value = parseSpecValue(item)
    return sanitizeHtml(value)
  }
  return 'N/A'
}

const specifications = computed(() => {
  const tech = props.trim?.technicalSpecification
  const result: Record<
    string,
    {
      title: string
      icon: string
      items: Array<{ label: string; value: string }>
    }
  > = {}

  if (!tech) return result

  const categoryMap: Record<string, { title: string; icon: string }> = {
    Engine: { title: 'Engine & Performance', icon: 'mdi-engine' },
    Transmission: { title: 'Transmission', icon: 'mdi-car-shift-pattern' },
    Mileage: { title: 'Fuel Economy', icon: 'mdi-gas-station' },
    'Exterior Dimensions': { title: 'Exterior Dimensions', icon: 'mdi-ruler' },
    'Interior Dimensions': { title: 'Interior Dimensions', icon: 'mdi-seat' },
    Weights: { title: 'Weight Information', icon: 'mdi-weight' },
    Suspension: { title: 'Suspension', icon: 'mdi-car-cog' },
    Brakes: { title: 'Brakes', icon: 'mdi-car-brake-alert' },
    'Wheels & Tires': { title: 'Wheels & Tires', icon: 'mdi-tire' },
    Steering: { title: 'Steering', icon: 'mdi-steering' },
  }

  Object.entries(tech).forEach(([category, data]) => {
    const categoryInfo = categoryMap[category]
    if (categoryInfo && data?.items && data.items.length > 0) {
      const sanitizeHtml = (text: string): string => {
        if (!text) return ''
        return text
          .replace(/<[^>]*>/g, '') // Remove HTML tags
          .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
          .replace(/&amp;/g, '&') // Replace &amp; with &
          .replace(/&lt;/g, '<') // Replace &lt; with <
          .replace(/&gt;/g, '>') // Replace &gt; with >
          .replace(/&quot;/g, '"') // Replace &quot; with "
          .replace(/&#39;/g, "'") // Replace &#39; with '
          .replace(/\s+/g, ' ') // Replace multiple spaces with single space
          .trim()
      }
      const items = data.items
        .map((item: string) => {
          // Find the separator colon - prefer one followed by space, and avoid colons inside parentheses
          // Look for pattern like "something (:1) - Rear: value" where the last colon is the separator
          let colonIndex = -1

          // First try to find ": " pattern (colon followed by space) that's not inside parentheses
          const colonSpaceIndex = item.indexOf(': ')
          if (colonSpaceIndex !== -1) {
            // Check if this colon is inside parentheses by counting open/close parentheses before it
            let openParens = 0
            for (let i = 0; i < colonSpaceIndex; i++) {
              if (item[i] === '(') openParens++
              if (item[i] === ')') openParens--
            }
            // If we're not inside parentheses, use this colon
            if (openParens === 0) {
              colonIndex = colonSpaceIndex
            }
          }

          // If no ": " found or it was inside parentheses, try finding the last colon
          if (colonIndex === -1) {
            const lastColonIndex = item.lastIndexOf(':')
            if (lastColonIndex !== -1) {
              colonIndex = lastColonIndex
            }
          }

          if (colonIndex !== -1) {
            const label = item.substring(0, colonIndex).trim()
            // Skip colon and any following space
            let valueStart = colonIndex + 1
            if (item.charAt(valueStart) === ' ') valueStart++
            const value = item.substring(valueStart).trim() || 'N/A'
            return {
              label: sanitizeHtml(label),
              value: sanitizeHtml(value),
            }
          }

          // No colon found, entire string is key, value is N/A
          return {
            label: sanitizeHtml(item),
            value: 'N/A',
          }
        })
        .filter(item => item.value && item.value !== 'N/A')

      if (items.length > 0) {
        result[category.toLowerCase().replace(/\s+/g, '_')] = {
          ...categoryInfo,
          items,
        }
      }
    }
  })

  return result
})
</script>

<style scoped>
.spec-value {
  font-weight: 500;
  color: var(--v-theme-on-surface-variant);
  font-size: 0.875rem;
}
</style>
